import React, { useEffect, useRef, useState } from "react";
import {
  FaInstagram,
  FaLinkedin,
  FaShieldAlt,
  FaLeaf,
  FaGlobeAmericas,
  FaHandshake,
  FaCheckCircle,
  FaBars,
  FaTimes,
  FaArrowUp,
} from "react-icons/fa";

export default function Home() {
  // --- Responsive navbar state ---
  const [menuOpen, setMenuOpen] = useState(false);
  const [showTop, setShowTop] = useState(false);

  // --- Auto-scroll rows ---
  const row1Ref = useRef(null);
  const row2Ref = useRef(null);

  useEffect(() => {
    // Back-to-top visibility
    const onScroll = () => setShowTop(window.scrollY > 400);
    window.addEventListener("scroll", onScroll);

    // Horizontal auto-scroll (marquee)
    const startScroller = (ref, speed = 0.6) => {
      if (!ref.current) return null;
      const el = ref.current;
      let rafId;
      let x = 0;
      const step = () => {
        x += speed;
        if (x >= el.scrollWidth - el.clientWidth) x = 0;
        el.scrollLeft = x;
        rafId = requestAnimationFrame(step);
      };
      rafId = requestAnimationFrame(step);
      return () => cancelAnimationFrame(rafId);
    };

    const stop1 = startScroller(row1Ref, 0.7);
    const stop2 = startScroller(row2Ref, 0.9);

    return () => {
      window.removeEventListener("scroll", onScroll);
      stop1 && stop1();
      stop2 && stop2();
    };
  }, []);

  // Smooth scrolling globally
  useEffect(() => {
    document.documentElement.classList.add("scroll-smooth");
  }, []);

  const closeMenu = () => setMenuOpen(false);

  // === PAGE ===
  return (
    <>
      {/* === NAVBAR (taller, larger logo, modern look) === */}
      <header className="fixed top-0 z-50 w-full bg-gradient-to-b from-gray-900/95 to-gray-900/80 backdrop-blur border-b border-white/10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <div className="h-16 md:h-20 flex items-center justify-between text-white">
            {/* Logo → back to top */}
            <a href="#home" className="flex items-center gap-3">
              <img
                src="/images/jendyco-logo2.png"
                alt="Jendyco Logo"
                className="h-10 md:h-40 w-auto drop-shadow-[0_0_8px_white]"
              />
            </a>

            {/* Desktop Nav with underline animation */}
            <nav className="hidden md:flex items-center gap-10 text-sm font-semibold uppercase tracking-wide">
              {[
                ["#who-we-are", "Who We Are"],
                ["#products", "Our Products"],
                ["#sourcing-advantage", "Sourcing Advantage"],
                ["#contact", "Contact"],
              ].map(([href, label]) => (
                <a
                  key={href}
                  href={href}
                  className="relative after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:w-0 after:bg-orange-400 hover:after:w-full after:transition-all"
                >
                  {label}
                </a>
              ))}
            </nav>

            {/* Mobile toggle */}
            <button
              className="md:hidden p-2 rounded hover:bg-white/10"
              aria-label="Toggle menu"
              onClick={() => setMenuOpen((v) => !v)}
            >
              {menuOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>

          {/* Mobile Drawer */}
          {menuOpen && (
            <div className="md:hidden pb-3 pt-2 border-t border-white/10">
              <nav className="flex flex-col gap-2 text-white/90 text-sm font-semibold uppercase">
                <a href="#who-we-are" className="px-2 py-2 rounded hover:bg-white/10" onClick={closeMenu}>Who We Are</a>
                <a href="#products" className="px-2 py-2 rounded hover:bg-white/10" onClick={closeMenu}>Our Products</a>
                <a href="#sourcing-advantage" className="px-2 py-2 rounded hover:bg-white/10" onClick={closeMenu}>Sourcing Advantage</a>
                <a href="#contact" className="px-2 py-2 rounded hover:bg-white/10" onClick={closeMenu}>Contact</a>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* === HERO (full height, no cut-off, aligned anchors) === */}
      <section
        id="home"
        className="relative pt-16 md:pt-20 min-h-[100vh] flex items-center justify-center px-4 sm:px-6"
      >
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('/images/background_main2.png')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/55 to-black/70" />

        <div className="relative z-10 text-center text-white max-w-4xl">
          {/* Small pill – sourcing note */}
          <div className="inline-flex items-center gap-2 text-xs md:text-sm bg-white/10 border border-white/20 text-white rounded-full px-3 py-1 mb-4">
            <span className="w-2 h-2 rounded-full bg-orange-400" />
            Currently sourcing from Peru
          </div>

          <h1 className="font-[Playfair] text-4xl md:text-5xl font-semibold tracking-wide drop-shadow-md">
            Global Trade. Local Roots.
          </h1>
          <p className="mt-4 text-lg md:text-xl font-light leading-relaxed">
            We connect buyers worldwide to Peruvian premium exports. Coffee, cacao, cotton, and more.
            Built on trust, delivered with precision.
          </p>

          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a
              href="#products"
              className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white text-base md:text-lg font-bold px-6 py-3 rounded-xl shadow-lg transition transform hover:scale-[1.02]"
            >
              Explore Products
            </a>
            <a
              href="#contact"
              className="bg-white/95 text-orange-600 font-bold text-base md:text-lg px-6 py-3 rounded-xl shadow-lg hover:bg-white transition transform hover:scale-[1.02]"
            >
              Contact Us to Buy
            </a>
          </div>

          {/* Expanded trust strip */}
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4 text-xs sm:text-sm">
            <div className="flex items-center justify-center gap-2">
              <FaShieldAlt className="text-orange-400 text-lg" />
              <span>U.S.-Registered Company</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <FaHandshake className="text-orange-400 text-lg" />
              <span>Direct, Verified Sourcing</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <FaLeaf className="text-orange-400 text-lg" />
              <span>Ethical & Sustainable</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <FaGlobeAmericas className="text-orange-400 text-lg" />
              <span>Export Docs & Logistics</span>
            </div>
          </div>
        </div>
      </section>

      {/* === WHAT WE OFFER (anchor offset so it doesn’t sit under nav) === */}
      <section
        id="What-We-Offer"
        className="bg-white px-5 sm:px-8 lg:px-10 xl:px-20 py-16 text-gray-800 text-center scroll-mt-28"
      >
        <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-[#001f3f]-500">What We Offer</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white border border-orange-200 rounded-2xl shadow-sm p-6 hover:shadow-md transition">
            <h3 className="text-lg md:text-xl font-semibold mb-2 text-orange-600">Curated Sourcing</h3>
            <p className="text-gray-700">
              High altitude Arabica coffee (87%+ purity), rich cacao, premium Pima cotton, and more vetted for quality and ethics.
            </p>
          </div>
          <div className="bg-white border border-orange-200 rounded-2xl shadow-sm p-6 hover:shadow-md transition">
            <h3 className="text-lg md:text-xl font-semibold mb-2 text-orange-600">Trade & Logistics</h3>
            <p className="text-gray-700">
              Export-ready documentation and global shipping support. On time, compliant, transparent.
            </p>
          </div>
          <div className="bg-white border border-orange-200 rounded-2xl shadow-sm p-6 hover:shadow-md transition">
            <h3 className="text-lg md:text-xl font-semibold mb-2 text-orange-600">Custom Requests</h3>
            <p className="text-gray-700">
              From baby alpaca to rare Andean goods tap into verified, responsible sources with real traceability.
            </p>
          </div>
        </div>
      </section>

      {/* === SOURCING ADVANTAGE (global—not Peru-only) === */}
      <section
        id="sourcing-advantage"
        className="bg-orange-50 px-5 sm:px-8 lg:px-10 xl:px-20 py-16 text-gray-800 text-center scroll-mt-28"
      >
        <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-orange-500">Sourcing Advantage</h2>
        <p className="text-base sm:text-lg max-w-4xl mx-auto leading-relaxed">
          We operate globally with deep relationships in Peru and beyond. You get consistent quality, dependable volumes,
          and ethical supply chains from origin to your door.
        </p>

        {/* Quick credibility stats (modern pattern) */}
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
          {[
            ["72h", "Initial Response"],
            ["FOB/CIF", "Incoterms Support"],
            ["Traceable", "Batch Control"],
            ["Multi-Origin", "Scalable Network"],
          ].map(([big, small]) => (
            <div key={big} className="bg-white rounded-xl shadow-sm border border-orange-100 p-5">
              <div className="text-3xl font-extrabold text-[#002b5c]">{big}</div>
              <div className="text-xs uppercase tracking-wide text-gray-500 mt-1">{small}</div>
            </div>
          ))}
        </div>
      </section>

      {/* === WHO WE ARE === */}
      <section
        id="who-we-are"
        className="bg-white px-5 sm:px-8 lg:px-10 xl:px-20 py-16 text-gray-800 text-center scroll-mt-28"
      >
        <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-orange-500">Who We Are</h2>
        <p className="text-base sm:text-lg max-w-3xl mx-auto leading-relaxed">
          Jendyco International is a familydriven trade hub. We build long term partnerships, protect transparency
          across the supply chain, and elevate producers on the global stage.
        </p>
        <div className="mt-8 grid sm:grid-cols-2 md:grid-cols-4 gap-4 text-left max-w-5xl mx-auto">
          {[
            ["U.S. Entity", "Registered and operating in the United States."],
            ["Quality Control", "Tight curation and batch traceability."],
            ["Ethical First", "Fair terms for farmers and herders."],
            ["Responsive", "Fast replies, clear timelines, real updates."],
          ].map(([title, body]) => (
            <div key={title} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <FaCheckCircle className="text-green-500" />
                <h4 className="font-semibold">{title}</h4>
              </div>
              <p className="text-sm text-gray-600">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* === CONTACT (anchor offset fixed) === */}
      <section
        id="contact"
        className="bg-white px-5 sm:px-8 lg:px-10 xl:px-20 py-16 text-gray-800 text-center scroll-mt-28"
      >
        <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-[#001f3f]-500">Contact Us</h2>
        <p className="text-base sm:text-lg mb-2">Ready to source or need a custom request?</p>
        <p className="text-lg sm:text-xl mb-6">
          📧{" "}
          <a
            href="mailto:info@jendy.co?subject=Jendyco%20Inquiry&body=Hi%20Jendyco%2C%0A%0AWe%E2%80%99re%20interested%20in%3A%20%5Bcoffee%20%7C%20cacao%20%7C%20pima%20%7C%20alpaca%5D%0AVolumes%2FTimelines%3A%0ACompany%3A%0ALocation%3A%0A%0AThanks!"
            className="text-blue-600 underline"
          >
            info@jendy.co
          </a>
        </p>
        <div className="mx-auto max-w-3xl text-sm text-gray-600 leading-relaxed">
          Include product, estimated volume, destination, and timeline. We’ll respond promptly with availability and next steps.
        </div>
        <div className="mt-6">
          <a
            href="mailto:info@jendy.co?subject=Start%20an%20Order%20with%20Jendyco"
            className="inline-block bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-semibold px-6 py-3 rounded-lg shadow-md transition"
          >
            Email Us to Start an Order
          </a>
        </div>
      </section>

      {/* === PRODUCTS === */}
      <section id="products" className="min-h-screen bg-gray-100 text-gray-900 scroll-mt-28">
        <div className="pt-24 md:pt-28 px-5 sm:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl font-bold text-[#002b5c] mb-10 tracking-wide">Our Products</h1>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 px-5 sm:px-8 lg:px-10 xl:px-20 mb-16">
          {[
            "Peruvian Arabica Coffee",
            "Cacao Beans",
            "Pima Cotton",
            "Camu Camu & Lucuma Powders",
          ].map((title, index) => {
            const descriptions = [
              "High-altitude Arabica (87%+ purity), handpicked and sun-dried for peak flavor and consistency.",
              "Traceable Peruvian cacao with rich cocoa butter — ideal for premium chocolate production.",
              "Extra-long staple Peruvian Pima cotton favored by luxury fashion houses worldwide.",
              "Camu Camu (vitamin C powerhouse) and Lucuma (“Gold of the Incas”) from the Amazon & Andes.",
            ];
            return (
              <div
                key={index}
                className="bg-white border-2 border-orange-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition"
              >
                <h2 className="text-lg md:text-xl font-bold text-[#002b5c] mb-2">{title}</h2>
                <p className="text-sm text-[#002b5c] leading-relaxed mb-4">{descriptions[index]}</p>
                <a
                  href="#contact"
                  className="inline-block mt-2 text-sm text-orange-600 hover:underline font-semibold"
                >
                  Contact Us to Order →
                </a>
                <p className="text-xs text-gray-500 italic mt-1">Typical MOQ: 500kg • FOB Available</p>
              </div>
            );
          })}
        </div>
{/* ——— BABY ALPACA (3 GRADES) ——— */}
<section className="px-5 sm:px-8 lg:px-10 xl:px-20 mb-16">
  <div className="rounded-3xl bg-gradient-to-br from-orange-50 via-white to-amber-50 border border-orange-100 shadow-sm px-6 sm:px-10 py-14 text-center">
    <h2 className="text-3xl md:text-4xl font-extrabold text-[#002b5c] mb-3 tracking-wide">
      Baby Alpaca Wool
    </h2>
    <p className="text-lg text-[#333] max-w-5xl mx-auto mb-10">
      Softer than cashmere, naturally thermo regulating, and hypoallergenic. Certified grades available to match your line.
    </p>

    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
      {/* Royal */}
      <div className="bg-white rounded-2xl border border-orange-100 p-6 text-left shadow-sm hover:shadow-md transition">
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-block w-2 h-2 rounded-full bg-orange-500" />
          <h3 className="text-xl font-bold text-[#002b5c]">Royal Alpaca</h3>
        </div>
        <p className="text-sm text-gray-700 mb-4">
          Ultra-fine, top-grade fleece for elite fashion houses; drape and handfeel that rivals cashmere.
        </p>
        <ul className="space-y-1 text-sm text-gray-600">
          <li className="flex items-center gap-2"><FaCheckCircle className="text-green-500" /> Micron-tested</li>
          <li className="flex items-center gap-2"><FaCheckCircle className="text-green-500" /> Origin-certified</li>
          <li className="flex items-center gap-2"><FaCheckCircle className="text-green-500" /> Dye-ready lots</li>
        </ul>
      </div>

      {/* Baby */}
      <div className="bg-white rounded-2xl border border-orange-100 p-6 text-left shadow-sm hover:shadow-md transition">
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-block w-2 h-2 rounded-full bg-orange-500" />
          <h3 className="text-xl font-bold text-[#002b5c]">Baby Alpaca</h3>
        </div>
        <p className="text-sm text-gray-700 mb-4">
          Luxury-soft staple for premium knitwear and outerwear; balanced warmth, weight, and price.
        </p>
        <ul className="space-y-1 text-sm text-gray-600">
          <li className="flex items-center gap-2"><FaCheckCircle className="text-green-500" /> Consistent fineness</li>
          <li className="flex items-center gap-2"><FaCheckCircle className="text-green-500" /> Lot traceability</li>
          <li className="flex items-center gap-2"><FaCheckCircle className="text-green-500" /> Mill-friendly</li>
        </ul>
      </div>

      {/* Alpaca (Classic) */}
      <div className="bg-white rounded-2xl border border-orange-100 p-6 text-left shadow-sm hover:shadow-md transition">
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-block w-2 h-2 rounded-full bg-orange-500" />
          <h3 className="text-xl font-bold text-[#002b5c]">Alpaca (Classic)</h3>
        </div>
        <p className="text-sm text-gray-700 mb-4">
          Durable, versatile fiber with a naturally premium feel—ideal for wider-market collections.
        </p>
        <ul className="space-y-1 text-sm text-gray-600">
          <li className="flex items-center gap-2"><FaCheckCircle className="text-green-500" /> Ethical shearing</li>
          <li className="flex items-center gap-2"><FaCheckCircle className="text-green-500" /> Bulk volumes</li>
          <li className="flex items-center gap-2"><FaCheckCircle className="text-green-500" /> Competitive lead times</li>
        </ul>
      </div>
    </div>

    <div className="mt-8">
      <a
        href="#contact"
        className="inline-block bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-semibold px-6 py-3 rounded-lg shadow-md transition"
      >
        Contact Us About Baby Alpaca
      </a>
    </div>
  </div>
</section>

{/* ——— VICUÑA (BY REQUEST) ——— */}
<section className="px-5 sm:px-8 lg:px-10 xl:px-20 mb-16">
  <div className="rounded-3xl bg-gradient-to-br from-[#f0f7ff] via-white to-[#eaf2ff] border border-blue-100 shadow-sm px-6 sm:px-10 py-14 text-center">
    <h2 className="text-3xl md:text-4xl font-extrabold text-[#002b5c] mb-3 tracking-wide">
      Vicuña Wool <span className="text-sm align-top ml-1 font-semibold text-blue-600">(By Request)</span>
    </h2>
    <p className="text-lg text-[#333] max-w-5xl mx-auto mb-6">
      The rarest natural fiber on Earth—lightweight, breathable, and exceptionally warm.
      Sourced under strict conservation guidelines and export controls.
    </p>

    {/* quick trust row */}
    <div className="grid sm:grid-cols-3 gap-4 max-w-4xl mx-auto mb-8">
      <div className="bg-white rounded-xl border border-blue-100 p-4 text-sm text-gray-700">
        <div className="flex items-center gap-2 mb-1"><FaCheckCircle className="text-green-500" /> Compliance-first</div>
        Verified permits and chain-of-custody.
      </div>
      <div className="bg-white rounded-xl border border-blue-100 p-4 text-sm text-gray-700">
        <div className="flex items-center gap-2 mb-1"><FaCheckCircle className="text-green-500" /> Traceable origin</div>
        Documentation from source to shipment.
      </div>
      <div className="bg-white rounded-xl border border-blue-100 p-4 text-sm text-gray-700">
        <div className="flex items-center gap-2 mb-1"><FaCheckCircle className="text-green-500" /> Export readiness</div>
        Coordinated logistics & paperwork.
      </div>
    </div>

    <p className="text-base text-gray-700 max-w-4xl mx-auto mb-8">
      Upon request, we can securely Source for qualified buyers through our conservation-aligned partners.
      
    </p>

    <a
      href="#contact"
      className="inline-block bg-white text-blue-700 border border-blue-200 hover:bg-blue-50 font-semibold px-6 py-3 rounded-lg shadow-md transition"
    >
      Request Vicuña Sourcing →
    </a>
  </div>
</section>

        {/* Auto-scrolling image rows (now smooth + seamless) */}
        <div className="bg-gray-100 px-5 sm:px-8 lg:px-10 xl:px-20 pb-16">
          <h2 className="text-2xl font-bold text-center mb-8 text-[#002b5c]">
            Our Coffee & Cacao in Motion
          </h2>

          {/* Row 1 */}
          <div ref={row1Ref} className="overflow-x-hidden whitespace-nowrap rounded-xl">
            <div className="inline-flex gap-4 pr-10">
              {[
                "JendycoFields1.jpg",
                "Jendycoffee-Branch2.JPG",
                "Jendycoffee-branch.JPG",
                "Jendycocoffeebeans-closeup.JPG",
              ]
                .concat([
                  "JendycoFields1.jpg",
                  "Jendycoffee-Branch2.JPG",
                  "Jendycoffee-branch.JPG",
                  "Jendycocoffeebeans-closeup.JPG",
                ])
                .map((filename, idx) => (
                  <img
                    key={`r1-${idx}`}
                    src={`/images/${filename}`}
                    alt={`Jendyco ${idx + 1}`}
                    className="inline-block w-[300px] h-[200px] object-cover rounded-xl shadow-md"
                  />
                ))}
            </div>
          </div>

          {/* Row 2 */}
          <div ref={row2Ref} className="overflow-x-hidden whitespace-nowrap rounded-xl mt-8">
            <div className="inline-flex gap-4 pr-10">
              {[
                "JendycoCoffee-Branch3.JPG",
                "JendycoCacao-Branch3.JPG",
                "JendycoCacao-Branch2.JPG",
                "JendycoCacao-Branch.JPG",
              ]
                .concat([
                  "JendycoCoffee-Branch3.JPG",
                  "JendycoCacao-Branch3.JPG",
                  "JendycoCacao-Branch2.JPG",
                  "JendycoCacao-Branch.JPG",
                ])
                .map((filename, idx) => (
                  <img
                    key={`r2-${idx}`}
                    src={`/images/${filename}`}
                    alt={`Jendyco ${idx + 5}`}
                    className="inline-block w-[300px] h-[200px] object-cover rounded-xl shadow-md"
                  />
                ))}
            </div>
          </div>
        </div>

        {/* CTA strip */}
        <div className="bg-[#002b5c] text-white text-center py-14 px-5">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3">Global Sourcing, Real Expertise</h2>
          <p className="max-w-3xl mx-auto mb-5 text-base sm:text-lg">
            From superfoods to heritage textiles, if it’s from Peru, we can source it. Tell us what you need.
          </p>
          <a
            href="#contact"
            className="inline-block bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-3 rounded-full shadow-md transition"
          >
            Talk to Our Team
          </a>
        </div>
      </section>

      {/* Back to top */}
      {showTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-5 right-5 z-50 p-3 rounded-full bg-black/70 text-white shadow hover:bg-black/80"
          aria-label="Back to top"
          
          title="Back to top"
        >
          <FaArrowUp />
        </button>
      )}

      {/* FOOTER */}
      <footer className="bg-gray-900 text-white text-center py-8 text-sm">
        <div className="mb-4 flex justify-center gap-8">
          <a
            href="https://www.instagram.com/jendyco_international"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
          >
            <FaInstagram className="text-[#E1306C] text-2xl hover:scale-110 transition" />
          </a>
          <a
            href="https://www.linkedin.com/in/jendyco-international-27647b36b"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="LinkedIn"
          >
            <FaLinkedin className="text-[#0077B5] text-2xl hover:scale-110 transition" />
          </a>
        </div>
        © {new Date().getFullYear()} Jendyco International. All rights reserved.
      </footer>
    </>
  );
}
